import { useState } from 'react'

interface OnboardingTourProps {
  onComplete: () => void
  onSkip: () => void
}

interface TourStep {
  id: number
  title: string
  description: string
  image: string
  features: string[]
}

const tourSteps: TourStep[] = [
  {
    id: 1,
    title: "Welcome to DateShake! 🎲",
    description: "Discover amazing date ideas tailored just for you and your partner.",
    image: "🎯",
    features: [
      "Personalized date recommendations",
      "Filter by location, budget, and interests",
      "Save your favorite date ideas"
    ]
  },
  {
    id: 2,
    title: "Find Your Perfect Match 💕",
    description: "Our smart algorithm learns your preferences to suggest the best dates.",
    image: "🤖",
    features: [
      "AI-powered recommendations",
      "Partner preference matching",
      "Real-time availability updates"
    ]
  },
  {
    id: 3,
    title: "Plan & Share Together 📅",
    description: "Coordinate with your partner and make every date special.",
    image: "👫",
    features: [
      "Shared date planning",
      "Calendar integration",
      "Easy partner coordination"
    ]
  }
]

export default function OnboardingTour({ onComplete, onSkip }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const step = tourSteps[currentStep]

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const skipTour = () => {
    onSkip()
  }

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex justify-center space-x-2">
        {tourSteps.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === currentStep ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>

      {/* Step Content */}
      <div className="text-center space-y-4">
        <div className="text-6xl mb-4">{step.image}</div>
        <h2 className="text-2xl font-bold text-black">{step.title}</h2>
        <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
        
        {/* Features List */}
        <div className="bg-gray-50 rounded-lg p-4 mt-6">
          <h3 className="font-semibold text-black mb-3">Key Features:</h3>
          <ul className="space-y-2 text-left">
            {step.features.map((feature, index) => (
              <li key={index} className="flex items-center text-gray-700">
                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-4">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 0}
          className="btn-nav px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={skipTour}
            className="btn-nav px-6 py-2"
          >
            Skip Tour
          </button>
          <button
            type="button"
            onClick={nextStep}
            className="btn-primary px-6 py-2"
          >
            {currentStep === tourSteps.length - 1 ? 'Get Started!' : 'Next'}
          </button>
        </div>
      </div>

      {/* Step Counter */}
      <div className="text-center text-sm text-gray-500">
        Step {currentStep + 1} of {tourSteps.length}
      </div>
    </div>
  )
}
